import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { EvidensiaClinic, getEvidensiaOpeningStatus } from './evidensia-clinics';

const TIMEZONE = 'Europe/Stockholm';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const isTime = (value: unknown): value is string =>
  typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
const isDate = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^20\d{2}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(`${value}T12:00:00Z`)) &&
  new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;

export type EvidensiaOpeningSchedule = {
  weekly: { day: number; opens: string; closes: string }[];
  exceptions: { from: string; through: string; opens: string | null; closes: string | null }[];
};

export function isEvidensiaOpeningSchedule(value: unknown): value is EvidensiaOpeningSchedule {
  if (!value || typeof value !== 'object') return false;
  const schedule = value as EvidensiaOpeningSchedule;
  return (
    Array.isArray(schedule.weekly) &&
    schedule.weekly.length > 0 &&
    schedule.weekly.every(
      (slot) =>
        slot &&
        Number.isInteger(slot.day) &&
        slot.day >= 0 &&
        slot.day <= 6 &&
        isTime(slot.opens) &&
        isTime(slot.closes),
    ) &&
    Array.isArray(schedule.exceptions) &&
    schedule.exceptions.every(
      (slot) =>
        slot &&
        isDate(slot.from) &&
        isDate(slot.through) &&
        slot.through >= slot.from &&
        ((slot.opens === null && slot.closes === null) ||
          (isTime(slot.opens) && isTime(slot.closes))),
    )
  );
}

export function parseEvidensiaOpeningSchedule(html: string): EvidensiaOpeningSchedule | null {
  const array = (value: unknown): unknown[] =>
    Array.isArray(value) ? value : value ? [value] : [];
  const time = (value: unknown) =>
    typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d(?::00)?$/.test(value)
      ? value.slice(0, 5)
      : null;
  const findClinic = (value: unknown): Record<string, unknown> | null => {
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findClinic(item);
        if (found) return found;
      }
    } else if (value && typeof value === 'object') {
      const node = value as Record<string, unknown>;
      if (array(node['@type']).includes('VeterinaryCare')) return node;
      return findClinic(node['@graph']);
    }
    return null;
  };

  for (const script of Array.from(
    html.matchAll(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi),
  )) {
    try {
      const clinic = findClinic(JSON.parse(script[1] ?? ''));
      if (!clinic) continue;
      const location =
        clinic.location && typeof clinic.location === 'object'
          ? (clinic.location as Record<string, unknown>)
          : clinic;
      const schedule: EvidensiaOpeningSchedule = { weekly: [], exceptions: [] };
      for (const entry of array(location.openingHoursSpecification)) {
        if (!entry || typeof entry !== 'object') continue;
        const slot = entry as Record<string, unknown>;
        const opens = time(slot.opens),
          closes = time(slot.closes);
        if (!opens || !closes) continue;
        for (const weekday of array(slot.dayOfWeek)) {
          const day =
            typeof weekday === 'string'
              ? WEEKDAYS.indexOf(weekday.replace(/^https?:\/\/schema\.org\//, ''))
              : -1;
          if (day !== -1) schedule.weekly.push({ day, opens, closes });
        }
      }
      for (const entry of array(
        location.specialOpeningHoursSpecification ?? clinic.specialOpeningHoursSpecification,
      )) {
        if (!entry || typeof entry !== 'object') continue;
        const slot = entry as Record<string, unknown>;
        if (
          !isDate(slot.validFrom) ||
          !isDate(slot.validThrough) ||
          slot.validThrough < slot.validFrom
        )
          continue;
        const opens = time(slot.opens),
          closes = time(slot.closes);
        // Schema.org represents a closed day as 00:00–00:00, or without times.
        if ((opens && closes) || (slot.opens === undefined && slot.closes === undefined))
          schedule.exceptions.push({
            from: slot.validFrom,
            through: slot.validThrough,
            opens: opens === closes ? null : opens,
            closes: opens === closes ? null : closes,
          });
      }
      if (isEvidensiaOpeningSchedule(schedule)) return schedule;
    } catch {
      // Ignore malformed or unrelated structured data.
    }
  }
  return null;
}

export function getNextEvidensiaOpening(
  clinic: EvidensiaClinic,
  schedule: EvidensiaOpeningSchedule | null,
  now: Date,
) {
  if (getEvidensiaOpeningStatus(clinic, now) !== 'closed') return null;
  const currentDate = formatInTimeZone(now, TIMEZONE, 'yyyy-MM-dd');
  const todayOpens = clinic.openingHours?.opensAt;
  if (todayOpens && new Date(todayOpens) > now)
    return {
      date: currentDate,
      time: formatInTimeZone(todayOpens, TIMEZONE, 'HH:mm'),
      daysAway: 0,
    };
  if (!schedule) return null;

  // Advance calendar dates, not 24-hour durations, to handle daylight-saving changes.
  for (let daysAway = 1; daysAway <= 7; daysAway++) {
    const date = new Date(`${currentDate}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + daysAway);
    const day = date.toISOString().slice(0, 10);
    const exceptions = schedule.exceptions.filter(
      (slot) => slot.from <= day && slot.through >= day,
    );
    const slots = exceptions.length
      ? exceptions
      : schedule.weekly.filter((slot) => slot.day === date.getUTCDay());
    const opening = slots
      .map((slot) => slot.opens)
      .filter((opens): opens is string => opens !== null)
      .sort()[0];
    if (!opening) continue;
    const instant = fromZonedTime(`${day}T${opening}:00`, TIMEZONE);
    if (instant > now && formatInTimeZone(instant, TIMEZONE, 'HH:mm') === opening)
      return { date: day, time: opening, daysAway };
  }
  return null;
}

export async function fetchEvidensiaOpeningSchedule(
  slug: string,
  { fetchFn = fetch, timeoutInMs = 4000 }: { fetchFn?: typeof fetch; timeoutInMs?: number } = {},
) {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return null;
  const url = `https://evidensia.se/klinik/${slug}/`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutInMs);
  try {
    const response = await fetchFn(url, { signal: controller.signal, redirect: 'manual' });
    if (!response.ok || (response.url && response.url !== url)) return null;
    return parseEvidensiaOpeningSchedule(await response.text());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
